<script setup>
import { toRefs, ref, watch } from "vue";
import { useVModel } from "@vueuse/core";
import AutoComplete from "primevue/autocomplete";
import ordersService from "@/services/orders";
import { useToast } from "vue-toast-notification";
import { Dialog, DialogPanel, DialogTitle } from "@headlessui/vue";
import ButtonWithSpinner from "@/components/forms/SharedComponents/ButtonWithSpinner";
import VSelectCaret from "@/components/shared/select/VSelectCaret";
import SimpleTextarea from "@/components/shared/SimpleTextarea";
import CloseIcon from "@/components/svg-icons/CloseIcon";
import { validate as validateUUID } from "uuid";
import {
  ERROR_TIMEOUT,
  MAX_NOTE_TEXTAREA_LIMIT,
  WITHDRAWN_REASONS,
  WITHDRAWN_REASONS_WITH_SUBSTITUTE_ORDER_ID,
  CANCELLATION_REASONS_OTHER,
} from "@/constants";

const toast = useToast();

const props = defineProps({
  modalIsOpen: {
    type: Boolean,
    default: true,
  },
  modelValue: {
    type: Object,
    default: () => {},
    required: true,
  },
});

const { modalIsOpen } = toRefs(props);

const isSaving = ref(false);

const emit = defineEmits(["closeModal", "update:modelValue", "saveOrder"]);
const orderData = useVModel(props, "modelValue", emit);

const isReasonOtherSelected = ref(false);
const needsSubstituteOrderID = ref(false);
const isOrderIdValid = ref(true);
const defaultWithdrawnReason = ref();
const customWithdrawnReasonText = ref();
const substituteOrder = ref({
  id: undefined,
  firstName: undefined,
  lastName: undefined,
  programSession: undefined,
});
const foundSubstituteOrder = ref();

watch(modalIsOpen, async (open) => {
  const order = orderData.value?.order;

  isOrderIdValid.value = true;
  defaultWithdrawnReason.value = order?.withdrawn_reason;
  isReasonOtherSelected.value =
    defaultWithdrawnReason.value === CANCELLATION_REASONS_OTHER;
  needsSubstituteOrderID.value = WITHDRAWN_REASONS_WITH_SUBSTITUTE_ORDER_ID.includes(
    defaultWithdrawnReason.value
  );
  customWithdrawnReasonText.value = order?.withdrawn_reason_other;

  if (open) {
    substituteOrder.value = {
      id: undefined,
      firstName: undefined,
      lastName: undefined,
      programSession: undefined,
    };

    const substitute_order_id = orderData.value.order?.substitute_order_id;
    if (substitute_order_id) {
      substituteOrder.value.id = substitute_order_id;
      await searchSubstituteOrderId(substitute_order_id);
    }
  }
});

const handleChangeReason = () => {
  isReasonOtherSelected.value =
    defaultWithdrawnReason.value === CANCELLATION_REASONS_OTHER;
  needsSubstituteOrderID.value = WITHDRAWN_REASONS_WITH_SUBSTITUTE_ORDER_ID.includes(
    defaultWithdrawnReason.value
  );
  if (!isReasonOtherSelected.value) {
    customWithdrawnReasonText.value = undefined;
  }
  if (!needsSubstituteOrderID.value) {
    substituteOrder.value.id = undefined;
  }
};

const searchSubstituteOrderIdEvent = async (event) => {
  const search = event.query.trim();
  await searchSubstituteOrderId(search);
};

const searchSubstituteOrderId = async (search) => {
  if (search.length >= 3 && validateUUID(search)) {
    try {
      isOrderIdValid.value = true;
      const substituteOrderResponse = await ordersService.getOne({
        order_id: search,
      });
      const data = substituteOrderResponse?.data?.data?.order;
      foundSubstituteOrder.value = [
        {
          id: search,
          firstName: data?.participant_first_name,
          lastName: data?.participant_last_name,
          programSession: data?.session_name,
        },
      ];
    } catch {
      isOrderIdValid.value = false;
      toast.open({
        message: "This does not match any existing orders",
        type: "error",
        position: "bottom",
        duration: ERROR_TIMEOUT,
      });
    }
  } else {
    foundSubstituteOrder.value = [];
    substituteOrder.value.firstName = "";
    substituteOrder.value.lastName = "";
    substituteOrder.value.programSession = "";
    isOrderIdValid.value = false;
  }
  setSubstituteOrder();
};

const setSubstituteOrder = () => {
  const found = foundSubstituteOrder.value?.[0];
  if (found) {
    substituteOrder.value.firstName = found.firstName;
    substituteOrder.value.lastName = found.lastName;
    substituteOrder.value.programSession = found.programSession;
  }
};

const handleCloseModal = () => {
  defaultWithdrawnReason.value = orderData.value.order.withdrawn_reason;
  customWithdrawnReasonText.value = undefined;
  substituteOrder.value.id = undefined;
  emit("closeModal");
};

const updateWithdrawnReason = async () => {
  isSaving.value = true;
  if (!substituteOrder.value?.id && needsSubstituteOrderID.value) {
    toast.open({
      message: "No substitute order linked. Saving without substitute order",
      type: "error",
      position: "bottom",
      duration: ERROR_TIMEOUT,
    });
  }

  if (!isOrderIdValid.value) {
    toast.open({
      message:
        "This does not match any existing orders. Saving without substitute order",
      type: "error",
      position: "bottom",
      duration: ERROR_TIMEOUT,
    });
    orderData.value.order.substitute_order_id = undefined;
  } else {
    if (substituteOrder.value.id && needsSubstituteOrderID.value) {
      orderData.value.order.substitute_order_id = substituteOrder.value.id;
    } else {
      orderData.value.order.substitute_order_id = undefined;
    }
  }

  orderData.value.order.withdrawn_reason = defaultWithdrawnReason.value;
  orderData.value.order.withdrawn_reason_other = isReasonOtherSelected.value
    ? customWithdrawnReasonText.value
    : "";
  emit("saveOrder");
  isSaving.value = false;
};
</script>

<template>
  <div v-if="modalIsOpen" class="relative z-50">
    <div
      class="fixed inset-0 z-40 hidden bg-university-primary opacity-50 sm:block"
    ></div>
    <Dialog
      :open="modalIsOpen"
      class="fixed inset-0 z-50 py-6 overflow-auto bg-white sm:bg-transparent sm:p-6 sm:p-12"
      @close-modal="handleCloseModal"
      @click.self="handleCloseModal"
    >
      <div
        class="max-w-4xl px-6 mx-auto bg-white sm:px-16 md:px-24 sm:py-20 sm:rounded-lg sm:shadow z-50 relative"
      >
        <div class="absolute xs:top-1r sm:top-3r md:top-3r right-3r">
          <button
            class="h-6 w-6 flex items-center justify-center"
            @click="handleCloseModal"
          >
            <CloseIcon size="28" stroke="#007f80" stroke-width="1.75" />
          </button>
        </div>
        <DialogPanel>
          <DialogTitle
            class="flex justify-center mt-6 mb-12 text-xl leading-tight text-center sm:text-2xl md:text-3xl"
            >Withdrawn Entire Order
          </DialogTitle>
          <div class="w-full">
            <div class="flex flex-col mt-5">
              <label for="withdrawn-modal-reason" class="common-label-text"
                >Reason for withdrawal</label
              >
              <VSelectCaret
                v-model="defaultWithdrawnReason"
                fulfillment-withdrawn-modal-data-testid="withdrawn-modal-reason"
                outer-classes="checkbox-placeholder"
                :options="WITHDRAWN_REASONS"
                :clearable="false"
                :disabled="isSaving"
                @update:modelValue="handleChangeReason"
              ></VSelectCaret>
            </div>
            <div v-if="isReasonOtherSelected" class="flex flex-col mt-5">
              <label
                for="withdrawn-modal-custom-reason"
                class="common-label-text"
                >Aditional Notes</label
              >
              <SimpleTextarea
                v-model="customWithdrawnReasonText"
                fulfillment-withdrawn-modal-data-testid="withdrawn-modal-custom-reason"
                label-name="Other Reason"
                :max-length="MAX_NOTE_TEXTAREA_LIMIT"
                :disabled="isSaving"
              />
            </div>
            <div v-if="needsSubstituteOrderID" class="flex-col mt-5">
              <label for="substitute-order-id" class="common-label-text"
                >Substitute Order ID</label
              >
              <div
                class="flex items-center border border-gray-300 rounded-md w-full"
              >
                <AutoComplete
                  v-model="substituteOrder.id"
                  fulfillment-withdrawn-modal-data-testid="substitute-order-id"
                  option-label="id"
                  class="flex-grow"
                  :input-class="['w-full border-none']"
                  :dropdown-class="['w-full border-none']"
                  :panel-class="'hidden'"
                  :invalid="!isOrderIdValid"
                  :suggestions="foundSubstituteOrder"
                  :empty-search-message="` `"
                  @complete="searchSubstituteOrderIdEvent"
                  @item-select="setSubstituteOrder"
                >
                  <template #empty></template>
                </AutoComplete>
                <img
                  src="/images/icon-search.svg"
                  class="m-auto px-2 h-4"
                  alt=""
                />
              </div>
            </div>
            <div
              v-if="
                needsSubstituteOrderID && substituteOrder.id && !isOrderIdValid
              "
              class="flex justify-between mt-5"
            >
              <span class="text-red-700 text-[0.875rem]"
                >No existing orders match this value.</span
              >
            </div>
            <div
              v-if="
                needsSubstituteOrderID && substituteOrder.id && isOrderIdValid
              "
              class="flex justify-between mt-5"
            >
              <div class="flex-1">
                <label for="first-name" class="common-label-text"
                  >First Name</label
                >
                <span>{{ substituteOrder.firstName }}</span>
              </div>
              <div class="flex-1">
                <label for="last-name" class="common-label-text"
                  >Last Name</label
                >
                <span>{{ substituteOrder.lastName }}</span>
              </div>
              <div class="flex-[2]">
                <label for="program-session" class="common-label-text"
                  >Program Session</label
                >
                <span>{{ substituteOrder.programSession }}</span>
              </div>
            </div>
          </div>
          <div class="mt-8 flex justify-center font-semibold">
            <ButtonWithSpinner
              ref="cancelBtn"
              fulfillment-withdrawn-modal-data-testid="cancel-btn"
              data-cy="cancel-btn"
              variant="secondary-outlined"
              custom-class="mr-5 w-107px"
              @click="handleCloseModal"
            >
              <span>Cancel </span>
            </ButtonWithSpinner>
            <ButtonWithSpinner
              ref="saveBtn"
              fulfillment-withdrawn-modal-data-testid="save-btn"
              :prop-loading="isSaving"
              data-cy="save-withdrawn-button"
              variant="secondary"
              variant-type="normal"
              custom-class="w-107px"
              @click="updateWithdrawnReason"
            >
              <span>Save</span>
            </ButtonWithSpinner>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  </div>
</template>
